"use client"

import styled from "styled-components"

const drifts = [
  {
    name: "Total US Stock: VTI",
    source: "Target 55%",
    value: "63%",
    trend: [40, 44, 48, 52, 58, 63],
  },
  {
    name: "Long-Term Bond: BLV",
    source: "Target 20%",
    value: "13%",
    trend: [24, 22, 20, 17, 15, 13],
  },
]

const Card = styled.section`
  border-radius: 1.5rem;
  background: var(--color-card);
  padding: 1.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border: 1px solid color-mix(in oklch, var(--color-border) 60%, transparent);
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--color-card-foreground);
  margin: 0;
`

const DateText = styled.span`
  font-size: 0.875rem;
  color: var(--color-muted-foreground);
`

const Note = styled.div`
  margin-top: 1rem;
  border-left: 2px solid var(--color-border);
  padding-left: 0.75rem;
`

const NoteText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--color-muted-foreground);
  text-wrap: pretty;
`

const SectionTitle = styled.h3`
  margin: 1.5rem 0 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-card-foreground);
`

const List = styled.ul`
  margin: 1rem 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const Row = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border-radius: 1rem;
  background: color-mix(in oklch, var(--color-muted) 50%, transparent);
  padding: 0.75rem 1rem;
  border: 1px solid color-mix(in oklch, var(--color-border) 50%, transparent);
`

const RowInfo = styled.div`
  min-width: 0;
`

const RowName = styled.p`
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-card-foreground);
`

const RowSource = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: var(--color-muted-foreground);
`

const RowRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const Tag = styled.p`
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-alert);
`

const TagDot = styled.span`
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 9999px;
  background: var(--color-alert);
`

const TagValue = styled.p`
  margin: 0.125rem 0 0;
  font-family: var(--font-mono);
  font-size: 0.875rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--color-card-foreground);
  text-align: right;
`

function Sparkline({ points }: { points: number[] }) {
  const w = 72
  const h = 30
  const min = Math.min(...points)
  const max = Math.max(...points)
  const range = max - min || 1
  const step = w / (points.length - 1)
  const coords = points.map((p, i) => ({
    x: i * step,
    y: h - ((p - min) / range) * (h - 6) - 3,
  }))
  const d = coords
    .map((c, i) => `${i === 0 ? "M" : "L"}${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
    .join(" ")
  const last = coords[coords.length - 1]
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden>
      <path
        d={d}
        fill="none"
        stroke="var(--color-alert)"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={last.x} cy={last.y} r={3} fill="var(--color-alert)" />
    </svg>
  )
}

export default function RebalanceCard() {
  return (
    <Card>
      <Header>
        <Title>Your rebalancing plan</Title>
        <DateText>Jul 16, 2026</DateText>
      </Header>

      <Note>
        <NoteText>
          We detected 2 holdings that have drifted from your target allocation.
          Here is what we observed:
        </NoteText>
      </Note>

      <SectionTitle>Portfolio drift from targets</SectionTitle>
      <NoteText style={{ marginTop: "0.25rem" }}>
        Two funds moved more than 5 points away from their target weights after
        the recent equity rally:
      </NoteText>

      <List>
        {drifts.map((d) => (
          <Row key={d.name}>
            <RowInfo>
              <RowName>{d.name}</RowName>
              <RowSource>{d.source}</RowSource>
            </RowInfo>
            <RowRight>
              <div>
                <Tag>
                  <TagDot />
                  Off target
                </Tag>
                <TagValue>{d.value}</TagValue>
              </div>
              <Sparkline points={d.trend} />
            </RowRight>
          </Row>
        ))}
      </List>
    </Card>
  )
}
